// packages/shared/src/admin.ts
//
// Admin RBAC — 단일 소스. server 와 admin-app 가 모두 이 파일을 import 한다.
//
// 룰:
// - account 매칭은 case-insensitive (Ethereum address 정규화).
// - env 는 'prod' 로 시작하면 prod, 아니면 non-prod. 'dev-w2', 'dev-w3', 'staging-w3',
//   'prod-w3' 같은 server appEnv 와 'dev'/'staging'/'prod' 같은 admin-app VerseEnv 둘 다 받는다.

export type Role =
  | 'super'
  | 'qa'
  | 'read'
  | 'redeemWorker'
  | 'leagueWorker'
  | 'gameValidationWorker'
  | 'mailSendWorker'
  // RB migration Phase 3/4: web2→web3 계정 마이그레이션 브리지 워커(헤드리스).
  | 'migrationBridgeWorker';

export type Permission =
  | 'user.read'
  | 'user.write'
  | 'ban.write'
  | 'seed.read'
  | 'seed.write'
  | 'maintenance.write'
  | 'globalState.read'
  | 'globalState.write'
  | 'league.snapshot'
  | 'manifest.read'
  | 'manifest.write'
  | 'worker.records'
  | 'worker.finalize'
  // RB-1715: 격리(QUARANTINE)된 랭킹 점수의 운영자 리뷰 — 큐 조회 + confirm(ban)/clear(복원).
  | 'worker.review'
  | 'glory.redeem'
  | 'mail.send'
  // RB migration Phase 3: web2 MigrationSourceHandler.finalizeMigrationSource — 브리지 전용.
  | 'migration.finalize'
  // RB migration Phase 4: web2 MigrationSourceHandler.listQueuedForBridge/claimForBridge —
  // 브리지 폴링 루프 전용(discovery + CAS claim).
  | 'migration.claim';

type Policy = 'always' | 'non-prod';

/**
 * Role → permission(or wildcard) → policy 매트릭스.
 * 와일드카드: '*' (모든 perm), '*.read' (suffix=.read 모든 perm).
 * 더 구체적인 키가 와일드카드보다 우선.
 */
type WildcardKey = '*' | '*.read';
type RolePolicy = Readonly<Partial<Record<Permission | WildcardKey, Policy>>>;

export const ROLE_ACCOUNTS: Readonly<Record<Role, readonly string[]>> = {
  super: [
    '0x2C88f2F06aa707E8a7b0241267a2d63AcAE515DC', // main
    '0xEf4Ca01Dd7BA5960d6ace0576BB60ed41791313e', // jiwon
    '0xBf27c217f40475030E0DBB8bf7c78B6CB827c5fE', // jaeho
    '0xf1625bfECAbB101a6bA2Dd33e3d26D951FF5e0F0', // yang
  ],
  qa: [
    '0xD755444d498020dE696c5e3EFd56ec5fEFD16831', // gyeonpyo
  ],
  read: [
    '0x67AC2640731e7a5b9709016039677e38d1A0269c', // garam
    '0xB5F40A00aA0724410a5ffb99B1A00213327F1Bd2', // yujeong
    '0xC15F99A08875031521fb760aAe103075E68522AD', // songgaram00
    '0x4E337e39d53f9a251c2e350c4d06e962886Cc91a', // engineering (all-env read-only)
  ],
  redeemWorker: [
    '0x3c33a45868997dAF527d047D003F80F20F2648c8', // glory 리딤 게이트웨이 전용 워커 주소
  ],
  leagueWorker: [
    '0x3c33a45868997dAF527d047D003F80F20F2648c8', // 리그 스냅샷 잡 전용 워커 주소
  ],
  gameValidationWorker: [
    '0x3c33a45868997dAF527d047D003F80F20F2648c8', // 안티치트 워커 전용 워커 주소
  ],
  mailSendWorker: [
    '0x3c33a45868997dAF527d047D003F80F20F2648c8', // 공유 워커/게이트웨이 계정 재사용 — mail-send 발송용
  ],
  migrationBridgeWorker: [
    '0x3c33a45868997dAF527d047D003F80F20F2648c8', // 공유 워커/게이트웨이 계정 재사용 — migration-bridge(Phase 4) 전용
  ],
};

const ROLE_POLICY: Readonly<Record<Role, RolePolicy>> = {
  super: { '*': 'always' },
  qa: {
    // '*' covers non-prod write/read. '*.read' lifts every *.read perm to always.
    // The two explicit keys below also lift non-.read read-only perms (manifest.read
    // is already covered by *.read but kept for symmetry with read role; worker.records
    // does not end in .read so it needs an explicit always grant on prod).
    '*': 'non-prod',
    '*.read': 'always',
    'manifest.read': 'always',
    'worker.records': 'always',
  },
  read: {
    '*.read': 'always',
    'manifest.read': 'always',
    'worker.records': 'always',
  },
  redeemWorker: {
    'glory.redeem': 'always',
    'user.read': 'always',
  },
  leagueWorker: {
    'league.snapshot': 'always',
  },
  gameValidationWorker: {
    'ban.write': 'always',
    'worker.records': 'always',
    'worker.finalize': 'always',
    'user.read': 'always',
    'manifest.read': 'always',
  },
  mailSendWorker: {
    'mail.send': 'always',
  },
  migrationBridgeWorker: {
    'migration.finalize': 'always',
    'migration.claim': 'always',
  },
};

const WORKER_ROLES: ReadonlySet<Role> = new Set([
  'redeemWorker',
  'leagueWorker',
  'gameValidationWorker',
  'mailSendWorker',
  'migrationBridgeWorker',
]);

function isProdEnv(env: string): boolean {
  return env.startsWith('prod');
}

function normalize(account: string): string {
  return account.trim().toLowerCase();
}

export function getAccountRoles(account: string): Role[] {
  if (!account) return [];
  const a = normalize(account);
  const roles: Role[] = [];
  for (const role of Object.keys(ROLE_ACCOUNTS) as Role[]) {
    if (ROLE_ACCOUNTS[role].some((addr) => addr.toLowerCase() === a)) {
      roles.push(role);
    }
  }
  return roles;
}

export function hasAnyAdminRole(account: string): boolean {
  return getAccountRoles(account).length > 0;
}

/** Most-specific match wins. Explicit key > '*.read' > '*'. */
function lookupPolicy(policy: RolePolicy, perm: Permission): Policy | undefined {
  const explicit = policy[perm];
  if (explicit !== undefined) return explicit;
  if (perm.endsWith('.read')) {
    const wild = policy['*.read'];
    if (wild !== undefined) return wild;
  }
  const star = policy['*'];
  return star;
}

export function hasPermission(account: string, perm: Permission, env: string): boolean {
  if (!env) return false;
  const roles = getAccountRoles(account);
  if (roles.length === 0) return false;
  const prod = isProdEnv(env);
  for (const role of roles) {
    const p = lookupPolicy(ROLE_POLICY[role], perm);
    if (p === 'always') return true;
    if (p === 'non-prod' && !prod) return true;
  }
  return false;
}

/**
 * 어드민 앱 좌하단 badge 의 한 줄 요약. role + env 에 따라 분기.
 * 여러 role 보유 시 가장 강한 role 기준 (super > qa > read > worker).
 */
export function getRoleCapabilitySummary(account: string, env: string): string {
  if (!env) return '';
  const roles = getAccountRoles(account);
  if (roles.length === 0) return '';
  if (roles.includes('super')) return 'Everything.';
  if (roles.includes('qa')) {
    return isProdEnv(env) ? 'Read-only on this verse.' : 'All actions.';
  }
  if (roles.includes('read')) return 'Read-only on all verses.';
  if (roles.some((r) => WORKER_ROLES.has(r))) {
    return 'Worker role — UI not intended.';
  }
  return '';
}
